/**
 * Estado de «Ver más» de la bitácora, como reductor PURO.
 *
 * Está fuera del hook para poder probarlo sin montar React: lo que importa de «Ver más» son
 * reglas —desde dónde arranca el cursor, cómo se acumulan las páginas, qué pasa si una falla—
 * y no el ciclo de vida del componente.
 */

export type OlderEventsState<T> = {
  /** Lo cargado con «Ver más», en orden (más nuevo primero), sin la primera página. */
  older: T[]
  /** El cursor de la próxima página, o `null` si no hay más. Opaco: se devuelve tal cual. */
  cursor: string | null
  status: 'idle' | 'loading' | 'error'
}

export type OlderEventsAction<T> =
  | { type: 'start' }
  | {
      type: 'loaded'
      page: { data: T[]; pagination: { hasMore: boolean; nextCursor: string | null } }
    }
  | { type: 'failed' }

/** El cursor sólo arranca si la primera página dice que hay más: un `nextCursor` suelto no basta. */
export function initialOlderEventsState<T>(first: {
  hasMore: boolean
  nextCursor: string | null
}): OlderEventsState<T> {
  return { older: [], cursor: first.hasMore ? first.nextCursor : null, status: 'idle' }
}

/** Se puede pedir otra página sólo con cursor y sin otra petición en vuelo. */
export function canLoadMore<T>(state: OlderEventsState<T>): boolean {
  return state.cursor !== null && state.status !== 'loading'
}

export function olderEventsReducer<T>(
  state: OlderEventsState<T>,
  action: OlderEventsAction<T>
): OlderEventsState<T> {
  switch (action.type) {
    case 'start':
      return canLoadMore(state) ? { ...state, status: 'loading' } : state
    case 'loaded':
      // Una respuesta que llega sin petición en vuelo (por ejemplo, tras descartar el estado)
      // no se acumula: duplicaría páginas.
      if (state.status !== 'loading') return state
      return {
        older: [...state.older, ...action.page.data],
        cursor: action.page.pagination.hasMore ? action.page.pagination.nextCursor : null,
        status: 'idle'
      }
    case 'failed':
      // Lo ya cargado y el cursor se conservan: reintentar pide la MISMA página.
      return state.status === 'loading' ? { ...state, status: 'error' } : state
  }
}
